import React from "react";
import { StorefrontHunterIcon, FilterIcon } from "./icons";
import { formatRating } from "./models";
import { facetScopeNote } from "@/lib/storefrontHunter/pageFacets";
import { RatingStar } from "@/components/common/primitives";

export function StorefrontPageHeader() {
  return (
    <section className="sfh-page-header">
      <div className="sfh-page-heading">
        <div className="sfh-page-hunter-icon">
          <StorefrontHunterIcon className="h-full w-full" />
        </div>
        <div>
          <div className="sfh-page-kicker">Product Research</div>
          <h1>Storefront Hunter</h1>
          <p>
            Analyze Walmart sellers, explore their catalog, monitor new products and find sourcing
            opportunities.
          </p>
        </div>
      </div>
      <div className="sfh-live-chip">● Live Walmart Data</div>
    </section>
  );
}

export function StorefrontSearchPanel({
  query,
  onQueryChange,
  onAnalyze,
  onOpenFilters,
  filterActiveCount = 0,
  loading = false,
}) {
  return (
    <section className="sfh-search-card">
      <div className="sfh-storefront-search-row">
        <div className="sfh-storefront-search-input">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
            <path d="M16 16L20 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            value={query}
            onChange={(e) => onQueryChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onAnalyze?.();
            }}
            placeholder="Paste Walmart seller URL or enter Seller ID"
            data-testid="sfh-seller-search"
          />
        </div>
        <button
          type="button"
          className="sfh-analyze-btn"
          onClick={() => onAnalyze?.()}
          disabled={loading}
          data-testid="sfh-analyze"
        >
          Analyze
        </button>
        <button
          type="button"
          className="sfh-filter-btn"
          onClick={() => onOpenFilters?.()}
          data-testid="sfh-open-filters"
        >
          <FilterIcon />
          Filters
          {filterActiveCount > 0 ? (
            <span className="sfh-filter-count">{filterActiveCount}</span>
          ) : null}
        </button>
      </div>
    </section>
  );
}

export function StoreSummary({
  store,
  brands = [],
  categories = [],
  brandsState = "complete",
  categoriesState = "complete",
  facetScope = null,
  storeWideBrands = null,
}) {
  if (!store) return null;
  const rating = formatRating(store.rating);
  const scopeLabel = facetScope?.label || "This Page";
  const brandNote = facetScopeNote(facetScope, facetScope?.brandKnownCount);
  const categoryNote = facetScopeNote(facetScope, facetScope?.categoryKnownCount);
  return (
    <section className="sfh-overview-grid" data-testid="sfh-store-summary">
      <article className="sfh-panel">
        <header className="sfh-panel-header">
          <h2>Store Details</h2>
        </header>
        <div className="sfh-detail-body">
          <div className="sfh-detail-row">
            <div>Seller Name</div>
            <div>
              {store.seller_name ? (
                <a className="sfh-blue-link" href={store.catalog_url || "#"}>
                  {store.seller_name}
                </a>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="sfh-detail-row">
            <div>Seller ID</div>
            <div>{store.seller_id || "—"}</div>
          </div>
          <div className="sfh-detail-row">
            <div>Rating</div>
            <div>
              {rating != null ? (
                <>
                  {rating} <RatingStar />
                </>
              ) : (
                "—"
              )}
            </div>
          </div>
          <div className="sfh-detail-row">
            <div>Reviews</div>
            <div>{store.review_count != null ? Number(store.review_count).toLocaleString() : "—"}</div>
          </div>
          <div className="sfh-detail-row">
            <div>Products</div>
            <div>{store.product_count != null ? Number(store.product_count).toLocaleString() : "—"}</div>
          </div>
          <div className="sfh-detail-row">
            <div>Last Visited</div>
            <div>{store.last_visited ? "Today" : "—"}</div>
          </div>
        </div>
      </article>

      <article className="sfh-panel sfh-panel-facets">
        <header className="sfh-panel-header">
          <h2>Top Brands</h2>
          <span className="sfh-panel-scope" data-testid="sfh-brands-scope">
            {scopeLabel}
          </span>
        </header>
        {brandsState === "loading" ? (
          <div className="sfh-facet-skeleton" data-testid="sfh-brands-loading" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="sfh-facet-skeleton-row" />
            ))}
          </div>
        ) : brands.length ? (
          <div className="sfh-facet-scroll">
            <table className="sfh-rank-table" data-testid="sfh-top-brands">
              <thead>
                <tr>
                  <th>Brand Name</th>
                  <th>{scopeLabel}</th>
                </tr>
              </thead>
              <tbody>
                {brands.slice(0, 10).map((b) => (
                  <tr key={b.name}>
                    <td>
                      <span className="sfh-blue-link sfh-facet-name">{b.name}</span>
                    </td>
                    <td>{b.count ?? b.product_count ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sfh-panel-empty" data-testid="sfh-brands-empty">
            {brandsState === "unavailable" ? "Brand data unavailable" : "No brand data available"}
          </div>
        )}
        {brands.length && brandNote ? (
          <p className="sfh-facet-note" data-testid="sfh-brands-scope-note">
            {brandNote}
          </p>
        ) : null}
        {storeWideBrands ? (
          <p className="sfh-facet-note" data-testid="sfh-store-wide-brands">
            Walmart lists {storeWideBrands.count.toLocaleString()}
            {storeWideBrands.lowerBound ? "+" : ""} brands across the entire catalog
            (per-brand counts not published)
          </p>
        ) : null}
      </article>

      <article className="sfh-panel sfh-panel-facets" data-testid="sfh-top-categories-panel">
        <header className="sfh-panel-header">
          <h2>Top Categories</h2>
          <span className="sfh-panel-scope" data-testid="sfh-categories-scope">
            {scopeLabel}
          </span>
        </header>
        {categoriesState === "loading" ? (
          <div className="sfh-facet-skeleton" data-testid="sfh-categories-loading" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="sfh-facet-skeleton-row" />
            ))}
          </div>
        ) : categories.length ? (
          <div className="sfh-facet-scroll">
            <table className="sfh-rank-table" data-testid="sfh-top-categories">
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>{scopeLabel}</th>
                </tr>
              </thead>
              <tbody>
                {categories.slice(0, 10).map((c) => (
                  <tr key={c.name}>
                    <td>
                      <span className="sfh-blue-link sfh-facet-name">{c.name}</span>
                    </td>
                    <td>{c.count ?? c.product_count ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sfh-panel-empty" data-testid="sfh-categories-empty">
            Category data unavailable
          </div>
        )}
        {categories.length && categoryNote ? (
          <p className="sfh-facet-note" data-testid="sfh-categories-scope-note">
            {categoryNote}
          </p>
        ) : null}
      </article>
    </section>
  );
}

const SKELETON_METRIC_LABELS = [
  "Storefront Price",
  "Buy Box",
  "Max Cost",
  "Sellers",
  "Buy Sources",
  "Monthly Sold",
];

/**
 * Loading placeholder rows. Reuses the existing product-card structure and
 * geometry — only the inner values become skeleton blocks. Screens read the
 * container's aria-busy; the rows themselves are never announced as data.
 */
export function StorefrontProductSkeleton({ count = 5 }) {
  return Array.from({ length: Math.max(0, count) }, (_, i) => (
    <article
      key={i}
      className="sfh-product-card sfh-product-card--skeleton"
      data-testid="sfh-product-skeleton"
      aria-hidden="true"
    >
      <div className="sfh-product-head">
        <div className="sfh-product-image">
          <span className="sfh-skeleton-block sfh-skeleton-thumb" />
        </div>
        <div className="sfh-product-info min-w-0">
          <div className="sfh-title-row">
            <div className="sfh-title-copy">
              <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-title" />
              <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-meta" />
            </div>
          </div>
        </div>
      </div>
      <div className="sfh-metrics">
        {SKELETON_METRIC_LABELS.map((label) => (
          <article className="sfh-metric" key={label}>
            <span className="sfh-metric-label">{label}</span>
            <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-metric" />
          </article>
        ))}
      </div>
      <div className="sfh-skeleton-roi">
        <span className="sfh-skeleton-block sfh-skeleton-line sfh-skeleton-roi-line" />
      </div>
    </article>
  ));
}

export function StorefrontResultsHeader({  visibleCount,
  totalCount,
  catalogScope = null,
  catalogComplete = false,
}) {
  const featured = catalogScope === "featured";
  const totalKnown =
    !featured && totalCount != null && Number.isFinite(Number(totalCount));
  let countLabel;
  if (featured) {
    countLabel =
      visibleCount === 1
        ? "1 featured product"
        : `${visibleCount} featured products`;
  } else if (totalKnown) {
    countLabel = `${visibleCount} of ${Number(totalCount).toLocaleString()} results`;
  } else {
    countLabel = `${visibleCount} product${visibleCount === 1 ? "" : "s"}`;
  }
  return (
    <section className="sfh-results-summary">
      <div className="sfh-results-summary-left">
        <span className="sfh-results-dot" />
        <div className="sfh-results-copy">
          <div className="sfh-results-title-row">
            <strong>
              {featured
                ? "Featured products from this seller"
                : "Products from this seller"}
            </strong>
            <span data-testid="sfh-result-count">{countLabel}</span>
          </div>
          {featured ? (
            <div className="sfh-featured-note" data-testid="sfh-featured-note">
              Walmart currently exposes these featured products for this seller.
            </div>
          ) : null}
        </div>
      </div>
      <div className="sfh-view-toggle-group">
        <button type="button" className="sfh-view-toggle active">
          Rows
        </button>
        <button type="button" className="sfh-view-toggle" disabled title="Cards view coming later">
          Cards
        </button>
      </div>
    </section>
  );
}
